import { Suspense, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Zap, Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

const isAuthBypass = import.meta.env.VITE_AUTH_BYPASS === 'true';

export function LoginPage() {
  return (
    <Suspense fallback={null}>
      <LoginContent />
    </Suspense>
  );
}

function LoginContent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signInWithGoogle } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(
    searchParams.get('error') === 'auth' ? 'Authentication failed. Please try again.' : null,
  );

  // Support ?redirect= param for OAuth consent flow
  const redirectTo = searchParams.get('redirect');

  async function handleGoogleSignIn() {
    setLoading(true);
    setError(null);
    try {
      await signInWithGoogle(redirectTo || undefined);
    } catch {
      setError('Failed to start Google sign-in. Please try again.');
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-base-100 flex items-center justify-center dot-grid relative">
      <div className="glass-card rounded-2xl p-8 w-full max-w-sm text-center space-y-6 animate-fade-in">
        {/* Logo */}
        <div>
          <div className="text-3xl font-extrabold tracking-tight">
            Hy<span className="text-primary">Pe</span>Ma
          </div>
          <p className="text-[8px] font-semibold uppercase tracking-[0.15em] text-base-content/30 mt-0.5">
            Hyper Personalized Mail
          </p>
        </div>

        <p className="text-sm text-base-content/50">
          Sign in to start sending hyper-personalized emails.
        </p>

        {/* Error */}
        {error && (
          <div className="text-sm text-error bg-error/10 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        {/* Google sign-in */}
        <button
          onClick={handleGoogleSignIn}
          disabled={loading}
          className="btn btn-primary w-full gap-2"
        >
          {loading ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <svg viewBox="0 0 24 24" width="18" height="18" className="shrink-0">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
          )}
          {loading ? 'Signing in…' : 'Sign in with Google'}
        </button>

        {/* Dev bypass */}
        {isAuthBypass && (
          <>
            <div className="divider text-xs text-base-content/20">or</div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-warning/10 text-warning text-xs font-semibold uppercase tracking-wider">
              <Zap size={12} />
              Development Mode
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="btn btn-outline btn-sm w-full"
            >
              Continue as Dev User
            </button>
          </>
        )}

        <p className="text-xs text-base-content/20">
          By signing in, you agree to our{' '}
          <a href="/terms" target="_blank" className="underline hover:text-base-content/40">Terms of Service</a>
          {' '}and{' '}
          <a href="/terms#dpa" target="_blank" className="underline hover:text-base-content/40">Data Processing Agreement</a>.
        </p>
      </div>
    </div>
  );
}
